/**
 * What search engines may read (GD-04): every public page, the pages anyone can open without an
 * account, and nothing else. robots.txt is written from this list (`robotsTxt`), the web build refuses
 * a sitemap that lists anything robots.txt doesn't allow, and the API serves these, and only these,
 * without `noindex` (bugs.md: robots.txt and the sitemap disagreed). Nothing about any member is ever
 * public: the map, grid, profiles, inbox, the Gate, settings, /v1 and the console stay disallowed.
 *
 * A path ending in `/*` is a section: its own page and every page under it.
 */
export const PUBLIC_PAGES = [
    '/',
    '/how-it-works',
    '/partners',
    '/learn/*',
    '/safety',
    '/whats-new/*',
    '/cruising/*',
    '/terms',
    '/privacy',
];
/**
 * Public pages Google Analytics never counts, even when the visitor allowed it (decision 87): reading
 * one could say something about someone's health, or that something happened to them.
 */
export const ANALYTICS_SKIP = [
    '/learn/data-health',
    '/learn/safety-sexual-health',
    '/learn/safety-if-something-happened',
];
/**
 * Not pages, so never in the sitemap, but a crawler needs them: the sitemaps themselves, llms.txt and
 * the agents' list of them (`/.well-known/ard.json`, GD-12), what an app page loads to draw itself
 * (its scripts and styles, the icons), and the few calls the public pages make. In robots.txt's own
 * syntax.
 */
const CRAWLER_NEEDS = [
    '/sitemap.xml$',
    '/sitemap-pages.xml$',
    '/sitemap-cities.xml$',
    '/llms.txt$',
    '/.well-known/ard.json$',
    '/assets/',
    '/icons/',
    '/og/',
    '/logo.svg$',
    '/favicon',
    '/manifest.webmanifest$',
    '/v1/me$',
    '/v1/learn/',
    '/v1/legal/',
];
/** Is this one of the public pages (or under a public section)? Ignores a trailing slash and the query. */
export function isPublicPage(path) {
    const p = (path.split(/[?#]/)[0] ?? '/').replace(/(.)\/+$/, '$1');
    return PUBLIC_PAGES.some((page) => page.endsWith('/*') ? p === page.slice(0, -2) || p.startsWith(page.slice(0, -1)) : p === page);
}
/**
 * The crawlers that only draw link previews (the card you see when a link is pasted into Facebook, X,
 * Slack, Discord, Telegram or WhatsApp). They fetch the one page someone shared and index nothing, so
 * they may read any page the app draws, invite links (/i/<code>) included, and the cards in /og/. Never
 * the API, the console, members' media or the map tiles. Without this group robots.txt kept them off
 * every invite and off the cards themselves, and Facebook showed no preview (lib/social.ts in the api).
 */
export const LINK_PREVIEW_BOTS = [
    'facebookexternalhit',
    'Facebot',
    'Twitterbot',
    'LinkedInBot',
    'Slackbot',
    'Slackbot-LinkExpanding',
    'Discordbot',
    'TelegramBot',
    'WhatsApp',
];
/**
 * Search engines and link-preview fetchers, by a piece of their user agent (matched in any case). They
 * get the same pages as anyone (decision 183: `/` redirects them like any visitor); only /cruising's
 * script uses this, to skip asking a crawler where it is.
 */
const CRAWLER_TOKENS = [
    // Google: search, the URL Inspection tool, Ads and Shopping checks
    'Googlebot',
    'Google-InspectionTool',
    'AdsBot-Google',
    'Storebot-Google',
    'Mediapartners-Google',
    // Bing, and its preview and ads fetchers
    'bingbot',
    'BingPreview',
    'adidxbot',
    'msnbot',
    'DuckDuckBot',
    'Applebot',
    'YandexBot',
    'Baiduspider',
    'PetalBot',
    ...LINK_PREVIEW_BOTS,
    'Pinterestbot',
    'redditbot',
    'Embedly',
];
// Letters, digits and hyphens only: nothing to escape.
/** The list as one pattern, for a page's own script to test `navigator.userAgent` with (/cruising). */
export const CRAWLER_PATTERN = CRAWLER_TOKENS.join('|');
const CRAWLER_RE = new RegExp(CRAWLER_PATTERN, 'i');
/** A crawler or link-preview bot. No user agent at all is not one. */
export function isCrawler(userAgent) {
    return !!userAgent && CRAWLER_RE.test(userAgent);
}
// One group per crawler, not one group naming them all: some robots.txt readers take only the last
// User-agent line of a group, and Facebook kept answering 403 for the cards with the shared group.
const PREVIEW_RULES = [
    'Allow: /',
    ...['/v1/', '/mod', '/media/', '/clips/', '/quarantine/', '/tiles/'].map((p) => `Disallow: ${p}`),
];
const PREVIEW_GROUP = LINK_PREVIEW_BOTS.map((bot) => [`User-agent: ${bot}`, ...PREVIEW_RULES, ''].join('\n')).join('\n');
/**
 * robots.txt where indexing is on: the public pages and what a crawler needs to draw them; nothing else.
 * A public page with a query string too (`/cruising?from=front-door`, where `/` sends a visitor, or a
 * shared link's `?src=`): each page's canonical drops it. Not `/?`, which is the app (decision 183).
 * With an IndexNow key, its file (`/<key>.txt`) too, so the engines it tells can check it (GD-11).
 */
export function robotsTxt(origin, indexNowKey) {
    const allow = PUBLIC_PAGES.flatMap((page) => {
        const base = page.endsWith('/*') ? page.slice(0, -2) : page;
        return [`${base}$`, ...(page.endsWith('/*') ? [`${base}/`] : []), ...(base === '/' ? [] : [`${base}?`])];
    });
    return [
        PREVIEW_GROUP,
        'User-agent: *',
        ...[...allow, ...CRAWLER_NEEDS, ...(indexNowKey ? [`${indexNowPath(indexNowKey)}$`] : [])].map((p) => `Allow: ${p}`),
        'Disallow: /',
        '',
        `Sitemap: ${origin.replace(/\/$/, '')}/sitemap.xml`,
        '',
    ].join('\n');
}
/**
 * Whether search engines may read anything here (GD-04): on in production unless SEARCH_INDEXING says
 * otherwise; anywhere else only when it says so. The API (lib/seo.ts) and the worker's IndexNow ping
 * (GD-11) both ask this.
 */
export function indexingOn(env) {
    const v = env.SEARCH_INDEXING ?? '';
    return v === 'true' || v === '1' || (v === '' && env.NODE_ENV === 'production');
}
/**
 * IndexNow (GD-11, decision 217): the key that proves the site is ours to api.indexnow.org, which
 * shares with Bing, Yandex and the others. 8 to 128 hex characters, in `INDEXNOW_KEY`. It's public by
 * design (served at `/<key>.txt`), but it's still never written to a log.
 */
export const INDEXNOW_KEY_PATTERN = /^[0-9a-fA-F]{8,128}$/;
/** Where the key file is served: `/<key>.txt`. */
export const indexNowPath = (key) => `/${key}.txt`;
/** robots.txt where indexing is off: nothing for search engines and no sitemap; link previews still work. */
export const ROBOTS_CLOSED = `${PREVIEW_GROUP}\nUser-agent: *\nDisallow: /\n`;
/**
 * Whether robots.txt lets a crawler read `path`, read as Google does (RFC 9309): the group naming
 * `agent` (its product token, any case), or `User-agent: *` when none does; `*` matches anything and a
 * final `$` ends the match, the longest matching rule wins and an Allow wins a tie. No matching rule
 * means allowed. For the checks that keep robots.txt and the sitemap in step, and the preview crawlers.
 */
export function robotsAllows(robots, path, agent = '*') {
    const groups = [];
    let open = false;
    for (const raw of robots.split(/\r?\n/)) {
        const line = raw.replace(/#.*$/, '').trim();
        const m = /^([A-Za-z-]+)\s*:\s*(.*)$/.exec(line);
        if (!m?.[1])
            continue;
        const key = m[1].toLowerCase();
        const value = (m[2] ?? '').trim();
        if (key === 'user-agent') {
            // A run of User-agent lines opens one group.
            if (!open)
                groups.push({ agents: [], rules: [] });
            open = true;
            groups[groups.length - 1]?.agents.push(value.toLowerCase());
            continue;
        }
        open = false;
        if ((key === 'allow' || key === 'disallow') && value)
            groups[groups.length - 1]?.rules.push({ allow: key === 'allow', pattern: value });
    }
    const want = agent.toLowerCase();
    const rules = (groups.find((g) => want !== '*' && g.agents.includes(want)) ??
        groups.find((g) => g.agents.includes('*')))?.rules ?? [];
    let best = null;
    for (const r of rules) {
        const anchored = r.pattern.endsWith('$');
        const body = anchored ? r.pattern.slice(0, -1) : r.pattern;
        const re = new RegExp(`^${body
            .split('*')
            .map((s) => s.replace(/[.+?^${}()|[\]\\]/g, '\\$&'))
            .join('.*')}${anchored ? '$' : ''}`);
        if (!re.test(path))
            continue;
        if (!best || r.pattern.length > best.length || (r.pattern.length === best.length && r.allow))
            best = { allow: r.allow, length: r.pattern.length };
    }
    return best?.allow ?? true;
}
/**
 * What a sitemap lists that it shouldn't: anything robots.txt doesn't allow, and, in a list of pages,
 * anything that isn't a public page. Empty when the two agree. The web build fails on any (apps/web
 * sitemap.plugin.ts), and the tests check the API's sitemaps the same way.
 */
export function sitemapDisagreements(sitemap, robots) {
    const pages = !sitemap.includes('<sitemapindex');
    return [...sitemap.matchAll(/<loc>([^<]*)<\/loc>/g)]
        .map((m) => (m[1] ?? '').replace(/&amp;/g, '&').replace(/^[a-z]+:\/\/[^/]*/i, '') || '/')
        .filter((path) => !robotsAllows(robots, path) || (pages && !isPublicPage(path)));
}
//# sourceMappingURL=seo.js.map